import i18next from '../../../i18n';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ButtonGroup, Center, Spinner } from 'src/components/atoms';
import { Modal } from 'src/components/molecules';
import { Callout } from 'src/components/molecules/Callout/Callout';
import { SlideOut } from 'src/components/molecules/SlideOut/SlideOut';
import { reportDetailsVm } from 'src/components/pages/Case/Case.slice';
import {
  ManagedChannelType,
  useGetChannelEntriesQuery,
} from 'src/graphql/generated';
import { useIsFeatureEnabled } from 'src/hooks';
import { FeatureFlag, Message, State } from 'src/types';
import { getUserCaseRole } from 'src/utils/analytics';
import {
  trackChatTranslationUpdatedEvent,
  TRANSLATION_UPDATE_SOURCE,
} from 'src/utils/analytics/track/messagesTranslation';
import { userHash } from 'src/utils/analytics/utils/userHash';
import { isBefore } from 'src/utils/time';
import { MessageInput } from './MessageInput';
import { MessagesList } from './MessageList';
import {
  ButtonWrapper,
  Container,
  Header,
  ModalDescription,
  ModalDescriptionBold,
} from './ReportMessages.styles';
import { useManagedChannel } from './useManagedChannel';

interface Props {
  open: boolean;
  setTranslateMessages: (translateMessage: boolean) => void;
  messages: Message[];
  closeMessages: () => void;
  sendMessage: (channel: string) => void;
  draftMessage: string;
  updateDraftMessage: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isAssignee: boolean;
  channel: string;
  isLoading: boolean;
}

export const ReportMessages = ({
  open,
  setTranslateMessages,
  messages,
  closeMessages,
  sendMessage,
  draftMessage,
  updateDraftMessage,
  isAssignee,
  channel,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const { data: channelEntriesData } = useGetChannelEntriesQuery(
    { channel },
    { enabled: open && Boolean(channel) },
  );

  const { channelEntries, hasSentMessage } = useManagedChannel(
    (channelEntriesData?.getChannelEntries as ManagedChannelType[]) ?? [],
  );

  const currentUser = useSelector(
    (state: State) => state.globalUsers.currentUser,
  );

  const caseById = useSelector((state: State) => state.case.caseById);

  const isDynamicTranslationsEnabled = useIsFeatureEnabled(
    FeatureFlag.DYNAMIC_TRANSLATIONS,
  );

  const TranslationOption = {
    ORIGINAL: t('public.ChatContainer.originalMessages'),
    TRANSLATED: t('public.ChatContainer.translatedMessages'),
  };

  const [translationOption, setTranslationOption] = React.useState(
    TranslationOption.ORIGINAL,
  );

  const trackTranslationUpdate = async (translate: boolean) => {
    if (!caseById || !currentUser?.id) {
      return;
    }

    const finalTrackChatTranslationUpdatedEventProps = {
      organisation_id: caseById?.organisation,
      user_role: getUserCaseRole(
        currentUser.id,
        caseById.CaseTeam,
        caseById.CaseLead?.id,
        caseById.Reports ?? [],
      ),
      source: TRANSLATION_UPDATE_SOURCE.RESHUB,
      report_id: caseById?.Reports?.[0]?.id,
      reported_category: caseById?.Reports?.[0]?.ReportedCategory?.name,
      determined_category: caseById?.Reports?.[0]?.DeterminedCategory?.name,
      user_id: await userHash(currentUser.id.toString()),
      translated: translate,
      language: translate ? i18next.language : undefined,
    };
    trackChatTranslationUpdatedEvent(
      finalTrackChatTranslationUpdatedEventProps,
    );
  };

  const setTranslation = React.useCallback(
    (option: string) => {
      if (option === TranslationOption.ORIGINAL) {
        setTranslationOption(TranslationOption.ORIGINAL);
        setTranslateMessages(false);
      } else {
        setTranslationOption(TranslationOption.TRANSLATED);
        setTranslateMessages(true);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setTranslateMessages],
  );

  React.useEffect(() => {
    const translation = currentUser?.translationEnabled
      ? TranslationOption.TRANSLATED
      : TranslationOption.ORIGINAL;
    setTranslation(translation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.translationEnabled, setTranslation]);

  const messagesWithType = messages.map((message) => ({
    ...message,
    type: 'message' as const,
  }));

  const sortedMessagesAndNotifications = [
    ...messagesWithType,
    ...channelEntries,
  ].sort((a, b) => {
    if (isBefore(a.createdAt, b.createdAt)) {
      return -1;
    }

    return 1;
  });

  const {
    permissions: { canViewReportMessages, canSendReportMessages },
  } = useSelector(reportDetailsVm({ reportId: channel }));

  const [openModal, setOpenModal] = React.useState(false);

  if (!canViewReportMessages) {
    return null;
  }

  const showMoreModal = () => {
    return (
      <Modal
        title={t('public.ChatContainer.titlev2')}
        setOpen={setOpenModal}
        open={openModal}
        onConfirm={() => setOpenModal(false)}
        size={'medium'}
        data-testid={'reshub-message-translation-learn-more-modal'}
        confirmButtonText={t('public.ChatContainer.understood')}
      >
        <ModalDescription>
          {t('public.ChatContainer.description')}
          <br />
          {t('public.ChatContainer.autoTranslate')}
          <br />
          {t('public.ChatContainer.changeInProfile')}{' '}
          <ModalDescriptionBold>
            {t('public.ChatContainer.caseSettings')}
          </ModalDescriptionBold>
          <br />
          <br />
          {t('public.ChatContainer.descriptionSecondHalf')}
        </ModalDescription>
      </Modal>
    );
  };

  return (
    <SlideOut
      isOpen={open}
      title="Message reporter"
      confirmButtonText="Send message"
      confirmButtonDataTestid="save-summary-changes"
      containerDataTestid="case-summary"
      dividerHasPadding={false}
      showFooter={false}
      onClose={closeMessages}
      data-testid="report-messages-slideout"
      onConfirm={() => sendMessage(channel)}
      applyButtonDisabled={!draftMessage}
      $width={'400px'}
      bodyStyle={{ margin: '0' }}
      headerChildren={
        isDynamicTranslationsEnabled && (
          <Header>
            <ButtonWrapper>
              <ButtonGroup
                options={[
                  TranslationOption.TRANSLATED,
                  TranslationOption.ORIGINAL,
                ]}
                selectedOption={translationOption}
                size="medium"
                setSelectedOption={(option) => {
                  setTranslation(option);
                  trackTranslationUpdate(
                    option === TranslationOption.TRANSLATED,
                  );
                }}
                buttonWidth="180px"
                data-testid="reshub-message-translation-button"
                buttonGroupOptionTestId={'opr-login-option'}
              />
            </ButtonWrapper>
            <Callout
              onClick={() => {
                setOpenModal(true);
              }}
              data-testid="reshub-message-translation-learn-more-button"
              header="Learn more about auto translations"
            />
          </Header>
        )
      }
    >
      <Container>
        {isLoading ? (
          <Center>
            <Spinner variant="secondary" size="medium" />
          </Center>
        ) : (
          <MessagesList messagesList={sortedMessagesAndNotifications} />
        )}
        {canSendReportMessages && (
          <MessageInput
            updateDraftMessage={updateDraftMessage}
            sendMessage={() => sendMessage(channel)}
            draftMessage={draftMessage}
            isAssigneeOrHasSentMessage={isAssignee || hasSentMessage}
          />
        )}
      </Container>
      {showMoreModal()}
    </SlideOut>
  );
};
